/* The fake API's power of attorney as HTML (contract 1.3 `PoaVersion.html`, w7-08).

   The accepted prototype's renderer (prototype/assets/app.js: poaDoc, poaFill, poaMark, poaJoin,
   poaText, poaSignedOn), from the prototype's strings (prototype/assets/poa-i18n/<locale>.json,
   generated from docs/poa-translations), shaped as the Intake API serves WordPress's fragment
   (w7-05 renders it, the API sanitises it):

   - WordPress's own markup and classes (delay-buddy-wp `templates/poa/poa.php`, w7-05: `poa-doc`,
     `poa-mandate`, `poa-flight-details`, `poa-label`, `poa-signatures`…), after the API's
     sanitiser (w7-07): only allowlisted tags, `class` on any of them, `lang`/`dir` on `div`,
     `span` and `bdi`; no images, so no logo and an empty countersignature box.
   - No draft banner and no "DRAFT · for counsel" tag (wave 7: none in production).
   - The two hooks agreed in w7-01: the signer's box is an empty
     `<div class="poa-signature-slot"></div>`, and the localised " (typed)" after their name is a
     `<span class="poa-typed-note">`, always present (the funnel shows it only for a typed name).

   `documentHash` here is SHA-256 over the document's inputs (versions, names, flight details,
   country, regime, and a revision the dev switch bumps), not over WordPress's canonical text
   (w7-05 defines that). The funnel treats it as opaque either way: it changes whenever anything
   printed changes, and never with the signing date or time zone.

   The strings load lazily (one chunk per locale, dev and tests only: an `http` build has no
   fake). */
import type { Locale, PoaVersion } from '../contract';

type Texts = Record<string, string> & { _language_names?: Record<string, string> };
interface IndexFile {
  locales: { code: string; dir: 'ltr' | 'rtl'; native: string | null; name_en: string }[];
  jurisdiction: { countries: Record<string, string>; airports: Record<string, string> };
}

const FILES = import.meta.glob('../../../../../prototype/assets/poa-i18n/*.json', { import: 'default' });
const fileFor = (name: string) => {
  const key = Object.keys(FILES).find((k) => k.endsWith(`/${name}.json`));
  if (!key) throw new Error(`poa-i18n: no ${name}.json`);
  return FILES[key]!() as Promise<unknown>;
};

let index: Promise<IndexFile> | null = null;
const loadIndex = () => (index ??= fileFor('index') as Promise<IndexFile>);
const texts = new Map<string, Promise<Texts>>();
const loadTexts = (l: string) => {
  if (!texts.has(l)) texts.set(l, fileFor(l) as Promise<Texts>);
  return texts.get(l)!;
};

/** The EU, the EEA, Switzerland and the UK (decisions 130 and 134; the prototype's JURIS_AREA). */
const JURIS_AREA = new Set(
  'AT BE BG HR CY CZ DK EE FI FR DE GR HU IE IT LV LT LU MT NL PL PT RO SK SI ES SE IS LI NO CH GB'.split(
    ' ',
  ),
);

/** Jurisdiction languages (poa-i18n/index.json `jurisdiction`; decisions 139, 141). Synchronous,
 *  so the fake can answer `jurisdiction` without loading the index; a test holds the two equal. */
export const JURIS_LANG: { countries: Record<string, Locale>; airports: Record<string, Locale> } = {
  countries: {
    AT: 'de', BE: 'fr', BG: 'bg', CH: 'fr', CY: 'el', CZ: 'cs', DE: 'de', DK: 'da', EE: 'et', ES: 'es',
    FI: 'fi', FR: 'fr', GB: 'en', GR: 'el', HR: 'hr', HU: 'hu', IE: 'en', IS: 'is', IT: 'it', LI: 'de',
    LT: 'lt', LU: 'fr', LV: 'lv', MT: 'en', NL: 'nl', NO: 'nb', PL: 'pl', PT: 'pt-PT', RO: 'ro', SE: 'sv',
    SI: 'sl', SK: 'sk',
  },
  airports: { BSL: 'fr', EAP: 'fr', MHQ: 'sv', MLH: 'fr' },
}; // prettier-ignore

export interface PoaAirport {
  iata: string;
  name: string;
  countryCode: string;
}

/** The deciding airport: the arrival, unless it is outside the area; then the departure. */
export function jurisdictionOf(from: PoaAirport | null, to: PoaAirport | null) {
  const ap = to && JURIS_AREA.has(to.countryCode) ? to : (from ?? to);
  const country = ap?.countryCode ?? 'GB';
  const locale: Locale = (ap && JURIS_LANG.airports[ap.iata]) || JURIS_LANG.countries[country] || 'en';
  return { country, locale };
}

export type PoaDisruption = 'delayed' | 'cancelled' | 'denied_boarding' | 'missed_connection';

/** Everything one power of attorney prints, except the signing date and zone. */
export interface PoaPlan {
  /** 1–2 locales, the signer's first; already through the levers. */
  locales: Locale[];
  country: string;
  regime: 'EC261' | 'UK261';
  signer: string;
  /** The adults it covers (the signer on their own document; nobody on a supplementary one). */
  pax: string[];
  minors: string[];
  /** The signer's own document (the guardian clause's `own_*`), or one for a minor added later. */
  own: boolean;
  pnr: string | null;
  ident: string | null;
  carrier: string | null;
  /** YYYY-MM-DD */
  date: string | null;
  from: PoaAirport | null;
  to: PoaAirport | null;
  disruption: PoaDisruption | null;
  /** Bumped by the fake's "document changed" switch. */
  revision: number;
}

const BANDS = { EC261: { a: [250, 400, 600], cur: 'EUR' }, UK261: { a: [220, 350, 520], cur: 'GBP' } };
const EVENT_KEY: Record<PoaDisruption, string> = {
  cancelled: 'disruption_cancelled',
  delayed: 'disruption_delayed',
  denied_boarding: 'disruption_denied_boarding',
  missed_connection: 'disruption_missed_connection',
};

const esc = (v: unknown) =>
  String(v ?? '').replace(
    /[&<>"']/g,
    (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c]!,
  );

/* In RTL versions the fixed Latin runs (DELAY BUDDY LLC, the address, 261/2004…) are isolated. */
const LATIN_RUN = /[A-Za-z0-9](?:[A-Za-z0-9 .,:'’/\-_&+()]*[A-Za-z0-9/)])?/g;
function poaText(s: string, rtl: boolean): string {
  if (!rtl) return esc(s);
  let out = '';
  let at = 0;
  for (const m of s.matchAll(LATIN_RUN)) {
    let run = m[0];
    if ((run.match(/\(/g) || []).length !== (run.match(/\)/g) || []).length)
      run = run.slice(0, run.search(/[()]/));
    run = run.replace(/[\s.,:'’\-_&+(]+$/, '');
    if (!/[A-Za-z/]/.test(run)) continue;
    out += esc(s.slice(at, m.index)) + `<bdi>${esc(run)}</bdi>`;
    at = m.index! + run.length;
  }
  return out + esc(s.slice(at));
}

/* What the renderer sets in bold (the prototype's poaMark: percentages, amounts with their
   currency, DELAY BUDDY LLC as the counterparty, a band's bare numbers). */
const B_ON = '';
const B_OFF = '';
const SP = '[ \\u00A0\\u202F]';
const NUM = '(?:\\d{1,3}(?:[,.\\u00A0\\u202F ]\\d{3})+(?!\\d)|\\d+)';
const RANGE = `${NUM}(?:${SP}?[–~～]${SP}?${NUM})?`;
const BOLD = new RegExp(
  `(ร้อยละ${SP}?\\d+|[%٪]${SP}?\\d+|\\d+${SP}?[%٪％])` +
    `|(\\{currency\\}${SP}?${RANGE}|${RANGE}${SP}?\\{currency\\})` +
    '|(DELAY BUDDY LLC)' +
    `|(?<![\\dA-Za-z])(${NUM})`,
  'g',
);
const PARTY = new Set(['intro_undersigned', 'price_intro']);
function poaMark(key: string, s: string): string {
  const band = key.startsWith('price_band_');
  return s.replace(BOLD, (m, pct, amt, co, num) =>
    pct || amt || (co && PARTY.has(key)) || (num && band) ? B_ON + m + B_OFF : m,
  );
}
const strong = (h: string) => `<strong>${h}</strong>`;
const poaFill = (s: string, vals: Record<string, string>, rtl: boolean) =>
  s
    .split(/(\{[a-z_]+\})/)
    .map((part, k) => (k % 2 ? (vals[part.slice(1, -1)] ?? '') : poaText(part, rtl)))
    .join('')
    .split(B_ON)
    .join('<strong>')
    .split(B_OFF)
    .join('</strong>');

function poaJoin(T: Texts, raw: string[], lang: string, rtl: boolean): string {
  const v = raw.map((n) => strong(`<bdi>${esc(n)}</bdi>`));
  if (v.length < 2) return v.join('');
  const last =
    lang === 'es' && /^h?[ií](?![aeoáéó])/i.test(raw[raw.length - 1]!) ? ' e ' : T.names_last_separator!;
  return v.slice(0, -1).join(poaText(T.names_separator!, rtl)) + poaText(last, rtl) + v[v.length - 1];
}

/** "Date signed": today in the viewer's zone, in the document's words (Gregorian years everywhere). */
function poaSignedOn(T: Texts, lang: string, rtl: boolean, now: Date, timeZone: string): string {
  let parts: Record<string, string> = {};
  try {
    parts = Object.fromEntries(
      new Intl.DateTimeFormat('en-US', { timeZone, year: 'numeric', month: 'numeric', day: 'numeric' })
        .formatToParts(now)
        .map((p) => [p.type, p.value]),
    );
  } catch {
    timeZone = 'UTC';
  }
  const day = Number(parts.day ?? now.getUTCDate());
  const month = Number(parts.month ?? now.getUTCMonth() + 1);
  return poaFill(
    T.date_signed_value!,
    {
      day: lang === 'fr' && day === 1 ? '1er' : String(day),
      month: poaText(T['month_' + String(month).padStart(2, '0')]!, rtl),
      year: parts.year ?? String(now.getUTCFullYear()),
      time_zone: `<bdi>${esc(timeZone)}</bdi>`,
    },
    rtl,
  );
}

const NO_SPACE = new Set(['zh-Hans', 'zh-Hant', 'ja']);

/** A language's name in another language: the browser's CLDR, else the file's snapshot. */
function nameIn(inLang: string, code: string, snapshot: Record<string, string> | undefined, ix: IndexFile) {
  try {
    const dn = new Intl.DisplayNames([inLang], { type: 'language', fallback: 'none' });
    if (new Intl.Locale(dn.resolvedOptions().locale).language === new Intl.Locale(inLang).language) {
      const n = dn.of(code);
      if (n && n !== code) return n;
    }
  } catch {
    /* no CLDR data for it here */
  }
  const meta = ix.locales.find((l) => l.code === code);
  return snapshot?.[code] ?? meta?.name_en ?? code;
}

/** One version: everything before the signature block (`body`, what the hash covers) and the block. */
function poaVersion(
  lang: Locale,
  T: Texts,
  p: PoaPlan,
  ix: IndexFile,
  now: Date,
  timeZone: string,
): { body: string; sig: string; dir: 'ltr' | 'rtl' } {
  const dir = ix.locales.find((l) => l.code === lang)?.dir ?? 'ltr';
  const rtl = dir === 'rtl';
  const tx = (s: string) => poaText(s, rtl);
  const fill = (k: string, vals: Record<string, string>) => poaFill(poaMark(k, T[k]!), vals, rtl);
  const val = (v: string | null | undefined) => `<bdi>${esc(v || T.empty_marker)}</bdi>`;
  const bold = (v: string | null | undefined) => (v ? strong(val(v)) : val(v));
  const guardian = p.minors.length
    ? (NO_SPACE.has(lang) ? '' : ' ') +
      fill(`guardian_clause_${p.own ? 'own' : 'supplementary'}_${p.minors.length > 1 ? 'many' : 'one'}`, {
        minor_names: poaJoin(T, p.minors, lang, rtl),
      })
    : '';
  /* The country in bold: the name alone where the form wraps it ("de l’Espagne"), the whole form
     where the name itself is inflected ("Spaniens"). */
  const cc = p.country;
  let regionName: string = cc;
  let ownName: string | null = null;
  try {
    regionName = new Intl.DisplayNames([lang], { type: 'region' }).of(cc) ?? cc;
    if (lang === 'en' || regionName !== new Intl.DisplayNames(['en'], { type: 'region' }).of(cc))
      ownName = regionName;
  } catch {
    /* CLDR's name unknown here */
  }
  const countryIn = (form: string) => {
    const at = ownName ? form.indexOf(ownName) : -1;
    const end = at + (ownName ?? '').length;
    if (at < 0 || /[\p{L}\p{M}]/u.test(form.slice(at - 1, at) + form.slice(end, end + 1)))
      return strong(tx(form));
    return tx(form.slice(0, at)) + strong(tx(ownName!)) + tx(form.slice(end));
  };
  const country = T['country_' + cc] ? countryIn(T['country_' + cc]!) : bold(regionName);
  const names = [...p.pax.map(bold), ...p.minors.map((n) => fill('minor_marker', { name: bold(n) }))];
  const row = (k: string, v: string) =>
    `<li><span class="poa-label">${tx(T[k]!)}${tx(T.label_colon!)}</span> ${v}</li>`;
  const R = BANDS[p.regime];
  const money = (n: number) => bold(`${n} ${R.cur}`);
  const amounts = {
    amount_short: money(R.a[0]!),
    amount_medium: money(R.a[1]!),
    amount_long: money(R.a[2]!),
    currency: val(R.cur),
    regime: val(p.regime),
  };
  const skip = p.regime === 'UK261' ? 'price_1500_to_3500km_ec261' : 'price_1500_to_3500km_uk261';
  const keys = Object.keys(T);
  const auth = keys.filter((k) => k.startsWith('auth_'));
  const price = keys.filter((k) => k.startsWith('price_') && k !== 'price_heading' && k !== skip);
  const langName = (c: string) => {
    const n = nameIn(lang, c, T._language_names, ix);
    return lang === 'vi' ? n.charAt(0).toLocaleLowerCase('vi') + n.slice(1) : n;
  };
  const [l1, l2] = p.locales;
  const ymd = p.date?.split('-').reverse().join('-');
  const ap = (a: PoaAirport | null) => a && `${a.name} - ${a.iata}`;
  const event = p.disruption && T[EVENT_KEY[p.disruption]];

  /* WordPress's fragment (delay-buddy-wp templates/poa/poa.php, w7-05), its images stripped by
     the API's sanitiser: the logo and the countersignature. */
  const body = [
    `<div class="poa-doc" lang="${lang}" dir="${dir}">`,
    `<p class="poa-mandate">${fill('intro_undersigned', { claimant_name: bold(p.signer), jurisdiction_country: country, guardian_clause: guardian })}</p>`,
    `<p class="poa-mandate">${tx(T.flight_details_intro!)}</p>`,
    '<ul class="poa-flight-details">',
    row('label_booking_reference', bold(p.pnr)),
    row('label_flight_number', bold(p.ident)),
    row('label_airline', bold(p.carrier)),
    row('label_flight_date', bold(ymd)),
    row('label_departure_airport', bold(ap(p.from))),
    row('label_arrival_airport', bold(ap(p.to))),
    row(
      names.length > 1 ? 'passenger_label_many' : 'passenger_label_one',
      names.join(tx(T.passenger_list_separator!)) || val(''),
    ),
    row('label_disruption_type', event ? strong(tx(event)) : tx(T.empty_marker!)),
    '</ul>',
    `<p class="poa-mandate">${tx(T.authorisations_intro!)}</p>`,
    `<ul class="poa-authorisations">${auth.map((k) => `<li>${tx(T[k]!)}</li>`).join('')}</ul>`,
    `<p class="poa-mandate">${tx(T.mandate_duration!)}</p>`,
    `<p class="poa-terms-ref">${tx(T.terms_reference!)}</p>`,
    `<h2 class="poa-price-heading">${tx(T.price_heading!)}</h2>`,
    `<div class="poa-price-section">${price.map((k) => `<p>${fill(k, amounts)}</p>`).join('')}</div>`,
    l2
      ? `<p class="poa-precedence">${fill('precedence_clause', {
          language_a: tx(langName(l1!)),
          language_b: tx(langName(l2)),
          jurisdiction_language: tx(langName(l2)),
        })}</p>`
      : '',
    `<p class="poa-signed-label">${tx(T.signed_label!)}</p>`,
  ].join('');
  const sig = [
    '<div class="poa-signatures">',
    '<div class="poa-signature-block"><div class="poa-signature-slot"></div><div class="poa-signature-rule"></div>',
    `<p class="poa-signature-caption">${bold(p.signer)}<span class="poa-typed-note">${tx(T.typed_caption!)}</span><br />`,
    `${fill('date_signed_caption', { date_signed: poaSignedOn(T, lang, rtl, now, timeZone) })}</p></div>`,
    '<div class="poa-signature-block"><div class="poa-countersignature"></div><div class="poa-signature-rule"></div>',
    `<p class="poa-signature-caption poa-company"><bdi>${esc(T.company_signature_caption)}</bdi></p></div>`,
    '</div></div>',
  ].join('');
  return { body, sig, dir };
}

/** The preview's `versions` (and nothing else: `documentHash` is `poaHash`, synchronous). */
export async function renderPoa(
  plan: PoaPlan,
  { now = new Date(), timeZone = 'UTC' }: { now?: Date; timeZone?: string } = {},
): Promise<PoaVersion[]> {
  const [ix, ...all] = await Promise.all([loadIndex(), ...plan.locales.map(loadTexts)]);
  return plan.locales.map((locale, k) => {
    const v = poaVersion(locale, all[k]!, plan, ix, now, timeZone);
    return {
      locale,
      role: k === 0 ? 'passenger' : 'jurisdiction',
      dir: v.dir,
      html: v.body + v.sig,
    };
  });
}

/** The document's fingerprint: SHA-256 (hex) of what it prints, minus the date and zone. */
export function poaHash(plan: PoaPlan): string {
  return sha256(JSON.stringify(plan));
}

/* ── SHA-256 (FIPS 180-4), synchronous: the fake checks a signature's hash inside its store
   update, where WebCrypto's promise can't be awaited. Dev and tests only. ── */
const K = Uint32Array.from(
  '428a2f98 71374491 b5c0fbcf e9b5dba5 3956c25b 59f111f1 923f82a4 ab1c5ed5 d807aa98 12835b01 243185be 550c7dc3 72be5d74 80deb1fe 9bdc06a7 c19bf174 e49b69c1 efbe4786 0fc19dc6 240ca1cc 2de92c6f 4a7484aa 5cb0a9dc 76f988da 983e5152 a831c66d b00327c8 bf597fc7 c6e00bf3 d5a79147 06ca6351 14292967 27b70a85 2e1b2138 4d2c6dfc 53380d13 650a7354 766a0abb 81c2c92e 92722c85 a2bfe8a1 a81a664b c24b8b70 c76c51a3 d192e819 d6990624 f40e3585 106aa070 19a4c116 1e376c08 2748774c 34b0bcb5 391c0cb3 4ed8aa4a 5b9cca4f 682e6ff3 748f82ee 78a5636f 84c87814 8cc70208 90befffa a4506ceb bef9a3f7 c67178f2'
    .split(' ')
    .map((h) => parseInt(h, 16)),
);
export function sha256(text: string): string {
  const bytes = new TextEncoder().encode(text);
  const len = bytes.length;
  const blocks = Math.ceil((len + 9) / 64);
  const m = new Uint8Array(blocks * 64);
  m.set(bytes);
  m[len] = 0x80;
  const view = new DataView(m.buffer);
  view.setUint32(m.length - 8, Math.floor((len * 8) / 2 ** 32));
  view.setUint32(m.length - 4, (len * 8) >>> 0);
  const H = Uint32Array.from([
    0x6a09e667, 0xbb67ae85, 0x3c6ef372, 0xa54ff53a, 0x510e527f, 0x9b05688c, 0x1f83d9ab, 0x5be0cd19,
  ]);
  const W = new Uint32Array(64);
  const rot = (x: number, n: number) => (x >>> n) | (x << (32 - n));
  for (let b = 0; b < blocks; b++) {
    for (let i = 0; i < 16; i++) W[i] = view.getUint32(b * 64 + i * 4);
    for (let i = 16; i < 64; i++) {
      const s0 = rot(W[i - 15]!, 7) ^ rot(W[i - 15]!, 18) ^ (W[i - 15]! >>> 3);
      const s1 = rot(W[i - 2]!, 17) ^ rot(W[i - 2]!, 19) ^ (W[i - 2]! >>> 10);
      W[i] = (W[i - 16]! + s0 + W[i - 7]! + s1) >>> 0;
    }
    const v = Array.from(H);
    for (let i = 0; i < 64; i++) {
      const [a, b2, c, d, e, f, g, h] = v as [number, number, number, number, number, number, number, number];
      const t1 = (h + (rot(e, 6) ^ rot(e, 11) ^ rot(e, 25)) + ((e & f) ^ (~e & g)) + K[i]! + W[i]!) >>> 0;
      const t2 = ((rot(a, 2) ^ rot(a, 13) ^ rot(a, 22)) + ((a & b2) ^ (a & c) ^ (b2 & c))) >>> 0;
      v.splice(0, 8, (t1 + t2) >>> 0, a, b2, c, (d + t1) >>> 0, e, f, g);
    }
    for (let i = 0; i < 8; i++) H[i] = (H[i]! + v[i]!) >>> 0;
  }
  return Array.from(H, (x) => x.toString(16).padStart(8, '0')).join('');
}
